import { Component, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { formatDate, CommonModule } from '@angular/common';
import { FinancePdfViewButtonComponent } from '../finance-pdf-view-button.component';
import { AccountsService } from 'src/app/accounts/accounts.service';
import { Receipt } from 'src/app/accounts/receipt/receipt.model';
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import * as XLSX from 'xlsx';
import { SessionStorageService } from "../../services/session-storage.service";
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from "primeng/progressbar";
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../shared/state/pending-action-tracker';

@Component({
	selector: 'app-receipt',
	templateUrl: './receipt.component.html',
	styleUrls: ['./receipt.component.css'],
	standalone: true,
	imports: [FinancePdfViewButtonComponent,
		CommonModule,
		BreadcrumbModule,
		TableModule,
		ButtonModule,
		TooltipModule,
		ProgressBarModule
	],
	providers: [
		AccountsService,
		SessionStorageService
	]
})
export class ReceiptComponent {
	readonly documentActions = new PendingActionTracker<number | undefined>();
	downloadError = '';

	receipts: Receipt[];
	first = 0;
	rows = 20;
	totalRequests = 0;
	isLoading = true;
	canViewReceipts = false;

	breadcrumbItems: MenuItem[];
	homeBreadcrumb: MenuItem;

	constructor(private accountsService: AccountsService,
		private sessionStorageService: SessionStorageService,
		private permissionService: PermissionService) {
		this.receipts = [];
		this.canViewReceipts = this.permissionService.hasPermission(app_permissions.accounts.receipt.view);
	}

	loadData(event: TableLazyLoadEvent) {
		if (!this.canViewReceipts) {
			this.isLoading = false;
			return;
		}
		this.isLoading = true;
		this.first = event.first ?? 0;
		this.rows = event.rows ?? 20;
		let page = event.first ?? 0;
		page = page / 20;
		let pageSize = event.rows ?? 20;
		if (pageSize == 0)
			pageSize = 20;
		this.accountsService.getReceipts(page, pageSize, this.sessionStorageService.getCompany()!.id, undefined).subscribe(receiptsData => {
			console.log(receiptsData);
			this.receipts = receiptsData.items;
			this.totalRequests = receiptsData.count;
			this.isLoading = false;
		});
	}

	downloadReceipt(receipt: Receipt) {
		if (!this.canViewReceipts) {
			return;
		}
		if (!receipt.receiptId || !this.documentActions.begin(receipt.receiptId)) return;
		this.downloadError = '';
		this.accountsService.downloadReceiptDocument(receipt.receiptId)
			.pipe(finalize(() => this.documentActions.end(receipt.receiptId)))
			.subscribe({ next: pdf => this.accountsService.downloadPdfDocument(pdf, 'receipt', receipt.receiptNumber, receipt.receiptId, receipt.postingStatus),
				error: err => this.downloadError = err?.status === 404 ? 'Original unavailable: no stored receipt PDF exists.' : 'The receipt PDF is unavailable.' });
	}

	ngOnInit(): void {
		this.breadcrumbItems = [{ label: 'Accounts', routerLink: '/accounts' }, { label: 'Receipts' }];
		this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
	}

	showDate(longDate: number) {
		return formatDate(new Date(longDate), 'dd-MM-yyyy HH:mm', 'en-US');
	}

	exportToExcel() {
		if (!this.canViewReceipts) {
			return;
		}
		// Create AOA
		const aoa: any[][] = [];
		aoa.push(['Receipt#', 'Receipt Date', 'Payment Transaction#', 'Amount', 'Card/Bank Name'],
			...this.receipts.map((receipt) => [
				receipt.receiptNumber,
				this.showDate(receipt.receiptDate!.getTime()),
				receipt.paymentTransactionNumber,
				receipt.country?.currencyCode + ' ' + receipt.amount,
				receipt.personalPaymentMethod?.paymentCardName
			])
		);

		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet(aoa);
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Receipts');

		const fileName = 'cargomate_receipts_export_' + new Date().toISOString() + '.xlsx';
		XLSX.writeFile(workbook, fileName);

		// Create a blob from the workbook
		const blob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })],
			{ type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
		);

		// Create a URL for the blob
		const url = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		a.target = '_blank';
		a.click();

		// Clean up by revoking the URL
		window.URL.revokeObjectURL(url);
	}
}
